import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/SimpleAuthContext';
import { useToast } from '../../contexts/ToastContext';
import { opportunityService } from '../../services/opportunityService';
import api from '../../services/apiClient';
import { lmsSkillCategoryKey, lmsSkillCategoryLabel, lmsSkillCategoryOptions } from '../../config/skillCategories';
import './OpportunityForm.css';

const OpportunityForm = ({ opportunityId = null, isEdit = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [skillCatalogue, setSkillCatalogue] = useState([]);
  const [skillSearch, setSkillSearch] = useState('');
  const [skillCategory, setSkillCategory] = useState('');
  const [skillCatalogueError, setSkillCatalogueError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    opportunity_type: 'job',
    start_date: '',
    end_date: '',
    application_deadline: '',
    capacity: 10,
    requirements: '',
    location: '',
    is_featured: false,
    related_course: '',
    skill_requirements: []
  });

  // Check if user has permission to create/edit opportunities
  useEffect(() => {
    if (!user || (!user.is_staff && !['admin', 'manager'].includes(user.role))) {
      navigate('/opportunities');
      return;
    }

    if (isEdit && opportunityId) {
      loadOpportunity();
    }
  }, [user, isEdit, opportunityId, navigate]);

  useEffect(() => {
    let active = true;
    api.get('/auth/skills/').then(({ data }) => {
      const rows = Array.isArray(data) ? data : data?.results || [];
      if (active) setSkillCatalogue(rows.filter(skill => skill?.is_active !== false && skill?.lms_skill_id));
    }).catch(() => {
      if (active) setSkillCatalogueError('The LMS skills catalogue could not be loaded. Refresh and try again.');
    });
    return () => { active = false; };
  }, []);

  const loadOpportunity = async () => {
    try {
      setLoading(true);
      const opportunity = await opportunityService.getOpportunity(opportunityId);
      
      // Format dates for input fields
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      };

      const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().slice(0, 16);
      };

      setFormData({
        title: opportunity.title || '',
        description: opportunity.description || '',
        opportunity_type: opportunity.opportunity_type || 'job',
        start_date: formatDate(opportunity.start_date),
        end_date: formatDate(opportunity.end_date),
        application_deadline: formatDateTime(opportunity.application_deadline),
        capacity: opportunity.capacity || 10,
        requirements: opportunity.requirements || '',
        location: opportunity.location || '',
        is_featured: opportunity.is_featured || false,
        related_course: opportunity.related_course?.id || '',
        skill_requirements: opportunity.skill_requirements || []
      });
    } catch (error) {
      console.error('Error loading opportunity:', error);
      setErrors({ general: 'Failed to load opportunity data.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const selectedSkillIds = new Set(formData.skill_requirements.map(item => String(item.skill_id)));
  const skillCategoryOptions = lmsSkillCategoryOptions(skillCatalogue);
  const filteredSkills = skillCatalogue.filter(skill => {
    const matchesText = skill.name.toLowerCase().includes(skillSearch.trim().toLowerCase());
    const matchesCategory = !skillCategory || lmsSkillCategoryKey(skill) === skillCategory;
    return matchesText && matchesCategory;
  });

  const toggleSkill = (skill) => {
    const skillId = String(skill.lms_skill_id);
    setFormData(previous => {
      const existing = previous.skill_requirements.find(item => String(item.skill_id) === skillId);
      return {
        ...previous,
        skill_requirements: existing
          ? previous.skill_requirements.filter(item => String(item.skill_id) !== skillId)
          : [...previous.skill_requirements, { skill_id: skillId, name: skill.name,
              category: lmsSkillCategoryKey(skill), proficiency_level: 'beginner' }]
      };
    });
  };

  const setSkillLevel = (skillId, proficiency_level) => {
    setFormData(previous => ({
      ...previous,
      skill_requirements: previous.skill_requirements.map(item => String(item.skill_id) === String(skillId)
        ? { ...item, proficiency_level } : item)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.application_deadline) {
      newErrors.application_deadline = 'Application deadline is required';
    } else {
      const deadline = new Date(formData.application_deadline);
      const now = new Date();
      if (deadline <= now) {
        newErrors.application_deadline = 'Application deadline must be in the future';
      }
    }

    if (!formData.capacity || formData.capacity < 1) {
      newErrors.capacity = 'Capacity must be at least 1';
    }

    if (formData.start_date && formData.end_date) {
      const startDate = new Date(formData.start_date);
      const endDate = new Date(formData.end_date);
      if (endDate <= startDate) {
        newErrors.end_date = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setErrors({});

      // Prepare data for submission
      const submitData = { ...formData };

      // Remove empty related_course and convert to null if empty
      if (!submitData.related_course || submitData.related_course === '') {
        delete submitData.related_course;
      }

      // Ensure status is set
      if (!submitData.status) {
        submitData.status = 'active';
      }

      console.log('🔍 Submitting opportunity data:', JSON.stringify(submitData, null, 2));
      console.log('🔍 Application deadline format:', submitData.application_deadline);
      console.log('🔍 Application deadline type:', typeof submitData.application_deadline);

      if (isEdit) {
        await opportunityService.updateOpportunity(opportunityId, submitData);
        showSuccess(`Opportunity "${submitData.title}" updated successfully!`, 5000);
      } else {
        await opportunityService.createOpportunity(submitData);
        showSuccess(`Opportunity "${submitData.title}" created successfully!`, 5000);
      }

      // Navigate back to opportunities dashboard
      navigate('/opportunities');
    } catch (error) {
      console.error('❌ Error submitting opportunity:', error);
      console.error('❌ Error response data:', JSON.stringify(error.response?.data, null, 2));
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Full error response:', error.response);

      if (error.response?.data) {
        // Handle field-specific errors from API
        const apiErrors = error.response.data;
        console.log('API Errors:', apiErrors);

        if (typeof apiErrors === 'object') {
          // Convert any array errors to strings
          const processedErrors = {};
          Object.keys(apiErrors).forEach(key => {
            if (Array.isArray(apiErrors[key])) {
              processedErrors[key] = apiErrors[key].join(', ');
            } else {
              processedErrors[key] = apiErrors[key];
            }
          });
          setErrors(processedErrors);

          // Show toast for general validation errors
          const errorMessages = Object.values(processedErrors).flat();
          if (errorMessages.length > 0) {
            showError(`Validation Error: ${errorMessages[0]}`, 6000);
          }
        } else {
          const errorMsg = `Failed to save opportunity: ${apiErrors}`;
          setErrors({ general: errorMsg });
          showError(errorMsg, 6000);
        }
      } else {
        const errorMsg = 'Network error. Please check your connection and try again.';
        setErrors({ general: errorMsg });
        showError(errorMsg, 6000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const opportunityTypes = [
    { value: 'job', label: 'Job' },
    { value: 'training', label: 'Training Course' },
    { value: 'internship', label: 'Internship' },
    { value: 'apprenticeship', label: 'Apprenticeship' },
    { value: 'workshop', label: 'Workshop' },
    { value: 'seminar', label: 'Seminar' },
    { value: 'conference', label: 'Conference' },
    { value: 'volunteer', label: 'Volunteer Opportunity' },
    { value: 'other', label: 'Other' }
  ];

  if (loading) {
    return (
      <div className="opportunity-form">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading opportunity data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="opportunity-form">
      <div className="form-header">
        <h1>{isEdit ? '✏️ Edit Opportunity' : '➕ Create New Opportunity'}</h1>
        <p>
          {isEdit 
            ? 'Update the opportunity details below.'
            : 'Fill in the details to create a new opportunity for learners.'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit} className="opportunity-form-content">
        {errors.general && (
          <div className="error-message general-error">
            {errors.general}
          </div>
        )}

        <div className="form-grid">
          {/* Basic Information */}
          <div className="form-section">
            <h3>📋 Basic Information</h3>
            
            <div className="form-group">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="e.g., Software Development Internship"
                className={errors.title ? 'error' : ''}
                required
              />
              {errors.title && <span className="error-text">{errors.title}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="opportunity_type">Type *</label>
              <select
                id="opportunity_type"
                name="opportunity_type"
                value={formData.opportunity_type}
                onChange={handleInputChange}
                className={errors.opportunity_type ? 'error' : ''}
                required
              >
                {opportunityTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.opportunity_type && <span className="error-text">{errors.opportunity_type}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Provide a detailed description of the opportunity..."
                rows="4"
                className={errors.description ? 'error' : ''}
                required
              />
              {errors.description && <span className="error-text">{errors.description}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="requirements">Requirements/Qualifications</label>
              <textarea
                id="requirements"
                name="requirements"
                value={formData.requirements}
                onChange={handleInputChange}
                placeholder="List the requirements, qualifications, or skills needed..."
                rows="3"
                className={errors.requirements ? 'error' : ''}
              />
              {errors.requirements && <span className="error-text">{errors.requirements}</span>}
            </div>

            <div className="form-group opportunity-skill-picker">
              <div className="opportunity-skill-picker__heading">
                <label htmlFor="opportunity-skill-search">Skills and minimum level</label>
                <span>{formData.skill_requirements.length} selected</span>
              </div>
              <p className="opportunity-skill-picker__hint">Choose skills from the shared LMS catalogue and set the minimum expected level.</p>
              {skillCatalogueError ? <div className="error-text" role="alert">{skillCatalogueError}</div> : (
                <>
                  <div className="opportunity-skill-picker__filters">
                    <input id="opportunity-skill-search" type="search" value={skillSearch}
                      onChange={event => setSkillSearch(event.target.value)} placeholder="Search skills…" />
                    <select aria-label="Filter skills by category" value={skillCategory}
                      onChange={event => setSkillCategory(event.target.value)}>
                      <option value="">All skill categories</option>
                      {skillCategoryOptions.map(category => <option value={category.id} key={category.id}>{category.label}</option>)}
                    </select>
                  </div>
                  <div className="opportunity-skill-picker__options" aria-label="Available skills">
                    {filteredSkills.length ? filteredSkills.map(skill => (
                      <label className="opportunity-skill-picker__option" key={skill.lms_skill_id}>
                        <input type="checkbox" checked={selectedSkillIds.has(String(skill.lms_skill_id))}
                          onChange={() => toggleSkill(skill)} />
                        <span><strong>{skill.name}</strong><small>{lmsSkillCategoryLabel(skill)}</small></span>
                      </label>
                    )) : <div className="opportunity-skill-picker__empty">No LMS skills match this search.</div>}
                  </div>
                  {formData.skill_requirements.length > 0 && (
                    <div className="opportunity-skill-picker__selected">
                      {formData.skill_requirements.map(item => (
                        <div className="opportunity-skill-picker__selected-row" key={item.skill_id}>
                          <span>{item.name}</span>
                          <select aria-label={`Minimum level for ${item.name}`} value={item.proficiency_level}
                            onChange={event => setSkillLevel(item.skill_id, event.target.value)}>
                            <option value="beginner">Beginner</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                          </select>
                          <button type="button" onClick={() => toggleSkill({ lms_skill_id: item.skill_id })}
                            aria-label={`Remove ${item.name}`}>Remove</button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                placeholder="e.g., Johannesburg, Remote, Hybrid"
                className={errors.location ? 'error' : ''}
              />
              {errors.location && <span className="error-text">{errors.location}</span>}
            </div>
          </div>

          {/* Dates and Capacity */}
          <div className="form-section">
            <h3>📅 Dates & Capacity</h3>
            
            <div className="form-group">
              <label htmlFor="application_deadline">Application Deadline *</label>
              <input
                type="datetime-local"
                id="application_deadline"
                name="application_deadline"
                value={formData.application_deadline}
                onChange={handleInputChange}
                className={errors.application_deadline ? 'error' : ''}
                required
              />
              {errors.application_deadline && <span className="error-text">{errors.application_deadline}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="start_date">Start Date</label>
              <input
                type="date"
                id="start_date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                className={errors.start_date ? 'error' : ''}
              />
              {errors.start_date && <span className="error-text">{errors.start_date}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="end_date">End Date</label>
              <input
                type="date"
                id="end_date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                className={errors.end_date ? 'error' : ''}
              />
              {errors.end_date && <span className="error-text">{errors.end_date}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="capacity">Capacity (Maximum Participants) *</label>
              <input
                type="number"
                id="capacity"
                name="capacity"
                value={formData.capacity}
                onChange={handleInputChange}
                min="1"
                max="1000"
                className={errors.capacity ? 'error' : ''}
                required
              />
              {errors.capacity && <span className="error-text">{errors.capacity}</span>}
            </div>

            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formData.is_featured}
                  onChange={handleInputChange}
                />
                <span className="checkmark"></span>
                Featured Opportunity
              </label>
              <small>Featured opportunities appear prominently on the dashboard</small>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/opportunities')}
            className="btn-outline"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span className="loading-spinner-small"></span>
                {isEdit ? 'Updating...' : 'Creating...'}
              </>
            ) : (
              isEdit ? 'Update Opportunity' : 'Create Opportunity'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OpportunityForm;
